import { m } from "$lib/paraglide/messages.js"

// Store the current competition language to be used as default
let currentCompetitionLanguage = "en"

/**
 * Sets the default competition language for translations
 * @param language The language code to set as default
 */
export function setDefaultLanguage(language: string | undefined): void {
  currentCompetitionLanguage = language || "en"
}

export function getCurrentLanguage(): string {
  return currentCompetitionLanguage
}

/**
 * Check if a language is right-to-left (RTL)
 * @param language The language code to check
 * @returns True if the language is RTL
 */
export function isRTL(language?: string): boolean {
  const currentLang = language || currentCompetitionLanguage
  const rtlLanguages = ['ar', 'ur', 'he', 'fa', 'ps']
  return rtlLanguages.indexOf(currentLang) !== -1
}

/**
 * Get the text direction for the current or specified language
 * @param language Optional language code
 * @returns 'rtl' for right-to-left languages, 'ltr' for left-to-right
 */
export function getTextDirection(language?: string): 'rtl' | 'ltr' {
  return isRTL(language) ? 'rtl' : 'ltr'
}

// Example Usage:
// t("widget.some.key");
// t("widget.some.key", "de");
// t("widget.some.key", { param: "value" });
// t("widget.some.key", "de", { param: "value" });

// Define a message function type
type MessageFunction = (
  params: Record<string, unknown>,
  options: { locale: string },
) => string

/**
 * Translates a message key using paraglide
 * @param key The message key to translate
 * @param langOrParams Optional language override or parameters
 * @param params Optional parameters for the message
 * @returns The translated message
 */
export function t(
  key: string,
  langOrParams?: string | Record<string, unknown>,
  params?: Record<string, unknown>,
): string {
  let locale = currentCompetitionLanguage
  let messageParams = {}

  // Handle different parameter patterns
  if (typeof langOrParams === "string") {
    locale = langOrParams
    messageParams = params || {}
  } else if (langOrParams && typeof langOrParams === "object") {
    messageParams = langOrParams
  }

  // Type casting with a specific type instead of any
  const messages = m as Record<string, MessageFunction>
  const messageFunc = messages[key]

  if (typeof messageFunc !== "function") {
    console.warn(`Translation key not found: ${key}`)
    return key
  }

  return messageFunc(messageParams, { locale })
}
